import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Mini-player track shape. `src` is the URL the `<audio>` element loads;
 * because the API endpoints live on the same origin (Vite dev proxy in
 * development, same domain in production), the auth cookie flows
 * automatically — no fetch+blob plumbing required.
 *
 * `id` lets feature lists know which row is currently playing without
 * comparing against the URL.
 */
export interface PlayerTrack {
  id: string
  src: string
  title: string
  subtitle?: string
}

export type PlayerState = {
  currentTrack: PlayerTrack | null
  /** 0..1 inclusive. Persisted under `spt_volume` (legacy parity). */
  volume: number
  /** Set when an in-flight playback fails (network / 403 / decode). */
  loadError: boolean

  play: (track: PlayerTrack) => void
  stop: () => void
  setVolume: (v: number) => void
  setLoadError: (v: boolean) => void
}

const DEFAULT_VOLUME = 0.7

const clampVolume = (v: number): number => {
  if (!Number.isFinite(v)) return DEFAULT_VOLUME
  if (v < 0) return 0
  if (v > 1) return 1
  return v
}

/**
 * Single global mini-player state. The component reads `currentTrack`
 * to decide whether to render at all, and `volume` to bind the
 * `<audio>` element. Volume is persisted to `localStorage.spt_volume`
 * (legacy parity); `currentTrack` is intentionally session-local —
 * a refresh stops playback.
 */
export const usePlayerStore = create<PlayerState>()(
  persist(
    (set, get) => ({
      currentTrack: null,
      volume: DEFAULT_VOLUME,
      loadError: false,

      play: (track) => {
        // Same id → bump src by re-setting reference, but the player
        // body is keyed by id so a same-id re-click is intentionally a
        // no-op (the audio is already loaded). Callers wanting to
        // restart playback should `stop()` first.
        if (get().currentTrack?.id === track.id) return
        set({ currentTrack: track, loadError: false })
      },

      stop: () => set({ currentTrack: null, loadError: false }),

      setVolume: (v) => set({ volume: clampVolume(v) }),

      setLoadError: (v) => set({ loadError: v }),
    }),
    {
      name: 'spt_volume',
      partialize: (s) => ({ volume: s.volume }),
      onRehydrateStorage: () => (state) => {
        if (!state) return
        // Defensive: anything came back malformed → reset to default.
        state.volume = clampVolume(state.volume)
      },
    },
  ),
)
